import {
  getWorkspaceEncounterHref,
  getWorkspaceHref,
} from './workspaceEncounterHrefs'
import { serializeEncounterRouteQueryParams } from './queryParams'
import { getTimelineEventHash } from '../../timeline/hooks/useHashTimelineId'
import type { components } from '@joyful/openapi/types.gen'

export type EncounterTransitionSource =
  components['schemas']['EncounterTransitionEvent']['source']

export const EncounterTransitionSourceLabel = {
  agent_run: `Agent run`,
  biller_action: `Action`,
  bulk_action: `Bulk action`,
  chat_message: `Agent chat`,
  encounter_tag: `Tag`,
  followup_flag: `Follow-up flag`,
  initialization: `Initialization`,
  manual_override: `Manual override`,
  payer_event: `Payer event`,
  practice_request: `Practice request`,
  resolution_event: `Resolution`,
  workflow_modification: `Workflow modification`,
  workflow_routing_backfill: `Workflow routing backfill`,
} as const

export function getEncounterTransitionSourceLabel(
  source: EncounterTransitionSource,
) {
  return EncounterTransitionSourceLabel[source.type]
}

export function getEncounterTransitionSourceHref({
  encounterId,
  orgSlug,
  source,
}: {
  encounterId: string
  orgSlug: string
  source: EncounterTransitionSource
}) {
  if (source.type === 'initialization') {
    return null
  }

  const encounterHref = getWorkspaceEncounterHref({ encounterId, orgSlug })

  if (source.type === 'practice_request') {
    return getWorkspaceHref(
      `/${orgSlug}/requests/${source.practice_request_id}`,
    )
  }

  if (source.type === 'biller_action') {
    return serializeEncounterRouteQueryParams(encounterHref, {
      actionId: source.biller_action_id,
      sidebar: 'action',
    })
  }

  if (source.type === 'chat_message') {
    return getEncounterAgentConversationHref({
      conversationId: source.conversation_id,
      encounterHref,
      hash: `#message-${source.chat_message_id}`,
    })
  }

  if (source.type === 'agent_run') {
    return getEncounterAgentConversationHref({
      conversationId: source.conversation_id,
      encounterHref,
    })
  }

  if (source.type === 'payer_event') {
    return getEncounterTimelineEventHref({
      encounterHref,
      id: source.payer_event_id,
    })
  }

  if (source.type === 'resolution_event') {
    return getEncounterTimelineEventHref({
      encounterHref,
      id: source.resolution_event_id,
    })
  }

  if (source.type === 'workflow_modification') {
    return getEncounterTimelineEventHref({
      encounterHref,
      id: source.workflow_modification_id,
    })
  }

  if (source.type === 'encounter_tag') {
    return getEncounterTimelineEventHref({
      encounterHref,
      id: source.tag_modification_id,
    })
  }

  return serializeEncounterRouteQueryParams(encounterHref, {
    sidebar: 'timeline',
  })
}

function getEncounterAgentConversationHref({
  conversationId,
  encounterHref,
  hash,
}: {
  conversationId: string | null | undefined
  encounterHref: string
  hash?: string
}) {
  if (!conversationId) {
    return null
  }

  const href = serializeEncounterRouteQueryParams(encounterHref, {
    conversation: conversationId,
    sidebar: 'agent',
  })
  return hash ? `${href}${hash}` : href
}

function getEncounterTimelineEventHref({
  encounterHref,
  id,
}: {
  encounterHref: string
  id: string
}) {
  return `${serializeEncounterRouteQueryParams(encounterHref, {
    sidebar: 'timeline',
  })}${getTimelineEventHash({ id, type: 'event' })}`
}
